import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {   
  saveAllTables,
  calculateFinancialSummary,
  setBool, 
  getVal
} from '../helpers/process_morningstar.js';

import {useAtomValue} from 'jotai';
import { emailAtom } from './Atoms.js';

import { fetchGraphData } from '../helpers/graph_middleware.js';

import AnnualFinancialSummary from './CompanyDetailComponents/AnnualFinancialSummary.js';
import FinancialSummary from './CompanyDetailComponents/FinancialSummary.js';
import AnnualDividends from './CompanyDetailComponents/AnnualDividends.js';
import Dividends from './CompanyDetailComponents/Dividends.js';
import AnnualCFandIncome from './CompanyDetailComponents/AnnualCFandIncome.js'; 
import CFandIncome from './CompanyDetailComponents/CFandIncome.js';
import AnnualInvestedCapital from './CompanyDetailComponents/AnnualInvestedCapital.js';
import InvestedCapital from './CompanyDetailComponents/InvestedCapital.js';
import AnnualAssetLife from './CompanyDetailComponents/AnnualAssetLife.js';
import AssetLife from './CompanyDetailComponents/AssetLife.js';
import AnnualSalvageValue from './CompanyDetailComponents/AnnualSalvageValue.js';
import SalvageValue from './CompanyDetailComponents/SalvageValue.js';
import AnnualReturnOnCapital from './CompanyDetailComponents/AnnualReturnOnCapital.js';
import ReturnOnCapital from './CompanyDetailComponents/ReturnOnCapital.js';
import Graphs from './CompanyDetailComponents/Graphs.js';
import MorningStar from './CompanyDetailComponents/MorningStar.js';
import ProgVargs from './CompanyDetailComponents/ProgVars.js'

import TableTabs from './CompanyDetailComponents/TableTabs.js';
import TickerSearch from './TickerSearch.js';
import Comparables from './CompanyDetailComponents/Comparables.js';
import Header from './CompanyDetailComponents/Header.js'

import PropTypes from 'prop-types';
import {
  Divider,
  Button,
  Container,
  CircularProgress,
  Drawer,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  IconButton,
  Select,
  Menu,
  MenuItem,
  Grid,
  Box,
  Tabs,
  Tab
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import { pdf } from "@react-pdf/renderer";
import { Pdf, DownloadInvoiceButton } from './Pdf.js';

const WEBAPI = process.env.REACT_APP_WEB_API;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function CompanyDetail() {
  const { ticker } = useParams();
  const upperTicker = ticker?.toUpperCase();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTable, setSelectedTable] = useState("Summary");
  const [msData, setMsData] = useState([]);
  const [capRD, setCapRD] = useState(null);
  const [intangibles, setIntangibles] = useState(null)
  const [includeOtherIncome, setIncludeOtherIncome] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [nav, setNav] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [chartImage, setChartImage] = useState([]);
  const [description, setDescription] = useState("");
  const [readyToDownload, setReadyToDownload] = useState(false);
  const [tablePeriod, setTablePeriod] = useState("Annual"); // new state for dropdown
  const [openMenu, setOpenMenu] = useState(false)
  const [graphInfo, setGraphInfo] = useState([])
  
  const chartRef = useRef(null);


  const userEmail = useAtomValue(emailAtom);
  
  const adminEmails = ["mkacur@fsavaluation.com", "ken@afxsoftware.com", "kenc@team-eagle.ca"];
  const isAdmin = adminEmails.includes(userEmail);

const tableComponents = [
  { tableName: "Summary", component: <Graphs onImagesReady={setChartImage} descriptionData={setDescription} /> },
  { tableName: "Financials", component: tablePeriod === "Quarterly" ? <FinancialSummary /> : <AnnualFinancialSummary /> },
  { tableName: "Dividends", component: tablePeriod === "Quarterly" ? <Dividends /> : <AnnualDividends /> },
  { tableName: "CFIRR And Returns", component: tablePeriod === "Quarterly" ? <ReturnOnCapital /> : <AnnualReturnOnCapital /> },
  { tableName: "Cash Flow & Income", component: tablePeriod === "Quarterly" ? <CFandIncome /> : <AnnualCFandIncome /> },
  { tableName: "Invested Capital", component: tablePeriod === "Quarterly" ? <InvestedCapital /> : <AnnualInvestedCapital /> },
  { tableName: "Asset Life", component: tablePeriod === "Quarterly" ? <AssetLife /> : <AnnualAssetLife /> },
  { tableName: "Salvage Value", component: tablePeriod === "Quarterly" ? <SalvageValue /> : <AnnualSalvageValue /> },
  ...(isAdmin
    ? [{ tableName: "MorningStar", component: <MorningStar /> },
      { tableName: "ProgVars", component: <ProgVargs /> }]
    : []),
  ];

  const handleTicker = (newTicker) => {
    if (!newTicker) return;
    //console.log(newTicker)
    navigate(`/${newTicker.toLowerCase()}`); // 👈 This triggers re-render with new ticker
  };

  // Fetch tables from backend and save in IndexedDB
  useEffect(() => {
    const fetchTables = async () => {
      try {

        // Reset UI before loading new data
        setTables([]);
        setError(null);
        setCompanyName("");

        // Delete old database first
        indexedDB.deleteDatabase(`CompanyTables`);

        if (upperTicker === undefined) {
            setError('Ticker is undefined');
            return;
        }

        const res = await axios.get(`${WEBAPI}/ticker/msdata/${upperTicker}`);

        setMsData(res.data);
        setCompanyName(res.data[0].tableData[3][1]);

        const tablesWithObjects = res.data.map(t => ({
          tableName: t.tableName,
          tableData: Array.isArray(t.tableData) ? t.tableData : []
        }));

        // Save backend tables in IndexedDB
        await saveAllTables(tablesWithObjects);

        setBool("capRD", true); // Default to true  
        setBool("includeOtherIncome", false); // Default to false
        setBool("selectedTax", false); // Default to false
        setBool('intangibles', false);
        localStorage.setItem('dateRange', '5Y')
        localStorage.setItem('period', 'Annual')


        setCapRD(getVal("capRD"));
        setIncludeOtherIncome(getVal("includeOtherIncome"));
        setSelectedTax(getVal("selectedTax"));
        setIntangibles(getVal('intangibles'))

        // Calculate Financial Summary using helper
        const financialSummaryData = await calculateFinancialSummary(capRD, includeOtherIncome, selectedTax);
        const graphData = await fetchGraphData(capRD, includeOtherIncome, selectedTax);

        setGraphInfo(graphData)



        setTables([
          { tableName: "Financial Summary", tableData: financialSummaryData },
          { tableName: "Dividends", tableData: financialSummaryData },
          { tableName: "CF and Income", tableData: financialSummaryData },
          { tableName: "Invested Capital", tableData: financialSummaryData },
          { tableName: "Asset Life", tableData: financialSummaryData },
          { tableName: "Salvage Value", tableData: financialSummaryData },
          { tableName: "Graphs", tableData: graphData },
          { tableName: "MorningStar" },
          { tableName: "ProgVars" }
        ]);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchTables();
  }, [upperTicker]);

  const handleChange = (event, newValue) => {
    setNav(newValue);
  };
  
  if (error) return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "90vh", // full screen height
      }}
      >
        Error: Not A Valid Ticker
      </div>
    );

  if (!tables.length) return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "90vh", // full screen height
      }}
    >
      <h2>Loading {ticker.toUpperCase()}...</h2>
      <CircularProgress />
    </div>
  );

    const selectedComponent = tableComponents.find(
      (t) => t.tableName === selectedTable
    )?.component;

  return (
    <Container sx={{ mt: 2, overflowY: 'hidden', mb: 2 }} maxWidth={false} disableGutters>

    <Box sx={{ display: 'flex', height: '100%', minHeight: '90vh', }}>
    
  {/* LEFT COLUMN (Desktop only) */}
  <Box
    sx={{
      display: { xs: "none", md: "flex" },
      flexDirection: "column",
      alignItems: "flex-start",
      width: 220,
      height: 400,
      p: 2,
    }}
  >
    <Box
      sx={{
        width: "100%",
        "& .MuiAutocomplete-root": { width: "100% !important" },
        "& .MuiInputBase-root": { width: "100% !important", fontSize: "0.75rem" },
        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
        "& .MuiAutocomplete-option": { fontSize: "0.75rem" },
        "& .MuiAutocomplete-popupIndicator": { display: "none" },
      }}
    >
      <TickerSearch handleAddTicker={handleTicker} fromCompanyDetail={true} />
    </Box>

    <TableTabs
      selectedTable={selectedTable}
      setSelectedTable={setSelectedTable}
      setTablePeriod={setTablePeriod}
      tableComponents={tableComponents}
    />
  </Box>


  {/* MOBILE DRAWER MENU */}
  <Drawer
    anchor="left"
    open={openMenu}
    onClose={() => setOpenMenu(false)}
    sx={{ display: { xs: "block", md: "none" } }}
  >
    <Box sx={{ width: 200, p: 2 }}>
      <Box
        sx={{
          width: "100%",
          "& .MuiAutocomplete-root": { width: "100% !important" },
          "& .MuiInputBase-root": { width: "100% !important", fontSize: "0.75rem" },
          "& .MuiInputLabel-root": { fontSize: "0.75rem" },
          "& .MuiAutocomplete-option": { fontSize: "0.75rem" },
          "& .MuiAutocomplete-popupIndicator": { display: "none" },
        }}
      >
        <TickerSearch handleAddTicker={handleTicker} fromCompanyDetail={true} />
      </Box>

      <TableTabs
        selectedTable={selectedTable}
        setSelectedTable={setSelectedTable}
        setTablePeriod={setTablePeriod}
        tableComponents={tableComponents}
      />
    </Box>
  </Drawer>

      {/* RIGHT COLUMN — Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 1,
          overflow: 'hidden',
          backgroundColor: '#fff',
          maxHeight: 'none'
        }}
      >
      
      <Box sx={{ mr: 1, }}>
        <Grid container alignItems="top" spacing={0.5}>
          <Grid item xs='auto'>
            <IconButton
              sx={{ display: { xs: "block", md: "none" } }}
              onClick={() => setOpenMenu(true)}
            >
              <MenuIcon />
            </IconButton>
          </Grid>

          <Grid item>
            {/* Flex container for TickerSearch and Typography */}
              <Header 
                graphData={graphInfo}
                pdfImage={chartImage} 
                pdfDescription={description}
              />
          </Grid>

          {/* Right-side content (optional) */}
          {/* <Grid 
            item
          >
          </Grid> */}
        </Grid>
      </Box >
        {selectedComponent}
      </Box>
        {selectedTable === "Summary" ? (
          <Comparables />
        ) : (
          <div></div>
        )}
    </Box>

    </Container>

  );
}